import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Consent state
  const [consent, setConsent] = useState({
    gdpr_consent: false,
    marketing_consent: false
  });

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Email change form
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });

  // Phone change form
  const [phoneForm, setPhoneForm] = useState({
    phone: ''
  });

  // Report problem form
  const [reportForm, setReportForm] = useState({
    subject: '',
    description: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchConsentStatus();
      setPhoneForm({ phone: user?.phone || '' });
    }
  }, [isAuthenticated, user]);

  const fetchConsentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/gdpr/consent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsent(prev => ({ ...prev, gdpr_consent: response.data.consent }));
    } catch (error) {
      console.error('Error fetching consent:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Passordene er ikke like');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/auth/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Passordet er endret');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setActiveSection(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunne ikke endre passord');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/auth/change-email`, {
        newEmail: emailForm.newEmail,
        password: emailForm.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser({ email: response.data.email });
      setSuccess('E-postadressen er endret');
      setEmailForm({ newEmail: '', password: '' });
      setActiveSection(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunne ikke endre e-post');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePhone = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/users/${user.id}`, {
        phone: phoneForm.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser({ phone: response.data.phone });
      setSuccess('Telefonnummeret er endret');
      setActiveSection(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunne ikke endre telefon');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConsent = async (type, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/gdpr/consent`, { consent: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsent(prev => ({ ...prev, [type]: value }));
    } catch (error) {
      console.error('Error updating consent:', error);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/gdpr/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `minitask-data-export-${user.id}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setSuccess('Data eksportert');
    } catch (error) {
      setError('Kunne ikke eksportere data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Er du sikker på at du vil slette kontoen din? Dette kan ikke angres. Alle dine data vil bli permanent slettet.'
    );
    
    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      'Siste sjanse! Skriv "SLETT" for å bekrefte.'
    );

    if (!doubleConfirm) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/gdpr/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      logout();
      navigate('/');
      alert('Kontoen din er slettet. Vi er lei for å se deg gå.');
    } catch (error) {
      setError('Kunne ikke slette konto. Prøv igjen senere.');
    } finally {
      setLoading(false);
    }
  };

  const handleReportProblem = async (e) => {
    e.preventDefault();
    // In a real app, this would send an email or create a support ticket
    setSuccess('Takk for tilbakemeldingen! Vi vil se på saken.');
    setReportForm({ subject: '', description: '' });
    setActiveSection(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            lock
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Du må være logget inn
          </h2>
          <Link 
            to="/logg-inn"
            className="inline-block bg-[#0A1929] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors"
          >
            Logg inn
          </Link>
        </div>
      </div>
    );
  }

  const menuItems = [
    {
      id: 'password',
      icon: 'lock',
      title: 'Endre passord',
      description: 'Oppdater passordet ditt'
    },
    {
      id: 'email',
      icon: 'mail',
      title: 'Endre e-post',
      description: 'Endre e-postadressen din'
    },
    {
      id: 'phone',
      icon: 'phone',
      title: 'Endre telefonnummer',
      description: 'Oppdater telefonnummeret ditt'
    },
    {
      id: 'privacy',
      icon: 'shield',
      title: 'Personvern og samtykke',
      description: 'Administrer dine datavalg'
    },
    {
      id: 'export',
      icon: 'download',
      title: 'Last ned mine data',
      description: 'Eksporter all din data (GDPR)'
    },
    {
      id: 'report',
      icon: 'flag',
      title: 'Rapporter et problem',
      description: 'Send oss tilbakemelding'
    },
    {
      id: 'delete',
      icon: 'delete_forever',
      title: 'Slett konto',
      description: 'Slett kontoen din permanent',
      danger: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/profil/${user?.id}`}
            className="inline-flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] mb-4 transition-colors"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Tilbake til profil
          </Link>
          <h1 className="font-heading font-bold text-2xl text-[#0A1929] flex items-center gap-2">
            <span className="material-symbols-outlined">settings</span>
            Innstillinger
          </h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined">error</span>
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <span className="material-symbols-outlined">check_circle</span>
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}

        {/* Menu Items */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {menuItems.map((item, index) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.id === 'export') {
                    handleExportData();
                  } else if (item.id === 'delete') {
                    handleDeleteAccount();
                  } else {
                    setActiveSection(activeSection === item.id ? null : item.id);
                  }
                }}
                className={`w-full p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left ${
                  item.danger ? 'text-red-600' : 'text-[#0A1929]'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  item.danger ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  <span className="material-symbols-outlined">{item.icon}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">{item.title}</h3>
                  <p className={`text-sm ${item.danger ? 'text-red-400' : 'text-slate-500'}`}>
                    {item.description}
                  </p>
                </div>
                {item.id !== 'export' && item.id !== 'delete' && (
                  <span className={`material-symbols-outlined transition-transform ${
                    activeSection === item.id ? 'rotate-180' : ''
                  }`}>
                    expand_more
                  </span>
                )}
              </button>

              {/* Expandable Sections */}
              {activeSection === item.id && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {/* Password Section */}
                  {item.id === 'password' && (
                    <form onSubmit={handleChangePassword} className="pt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Nåværende passord
                        </label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                          required
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Nytt passord
                        </label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          required
                          minLength={8}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Bekreft nytt passord
                        </label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          required
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#0A1929] text-white py-2 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Lagrer...' : 'Endre passord'}
                      </button>
                    </form>
                  )}

                  {/* Email Section */}
                  {item.id === 'email' && (
                    <form onSubmit={handleChangeEmail} className="pt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Ny e-postadresse
                        </label>
                        <input
                          type="email"
                          value={emailForm.newEmail}
                          onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                          required
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Bekreft med passord
                        </label>
                        <input
                          type="password"
                          value={emailForm.password}
                          onChange={(e) => setEmailForm(prev => ({ ...prev, password: e.target.value }))}
                          required
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#0A1929] text-white py-2 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Lagrer...' : 'Endre e-post'}
                      </button>
                    </form>
                  )}

                  {/* Phone Section */}
                  {item.id === 'phone' && (
                    <form onSubmit={handleChangePhone} className="pt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Telefonnummer
                        </label>
                        <input
                          type="tel"
                          value={phoneForm.phone}
                          onChange={(e) => setPhoneForm({ phone: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                          placeholder="12345678"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#0A1929] text-white py-2 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50"
                      >
                        {loading ? 'Lagrer...' : 'Lagre telefonnummer'}
                      </button>
                    </form>
                  )}

                  {/* Privacy Section */}
                  {item.id === 'privacy' && (
                    <div className="pt-4 space-y-4">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-[#0A1929]">Databehandling</h4>
                          <p className="text-xs text-slate-500">
                            Tillat oss å behandle dine data for å levere tjenesten
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consent.gdpr_consent}
                            onChange={(e) => handleUpdateConsent('gdpr_consent', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#1E3A5F] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1E3A5F]"></div>
                        </label>
                      </div>

                      <div className="text-xs text-slate-500 p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium text-[#1E3A5F] mb-1">Dine rettigheter (GDPR)</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Rett til innsyn i dine data</li>
                          <li>Rett til å korrigere feil data</li>
                          <li>Rett til å slette dine data</li>
                          <li>Rett til dataportabilitet</li>
                          <li>Rett til å trekke samtykke</li>
                        </ul>
                      </div>

                      <Link
                        to="/personvern"
                        className="block text-center text-sm text-[#1E3A5F] hover:underline"
                      >
                        Les vår personvernerklæring
                      </Link>
                    </div>
                  )}

                  {/* Report Section */}
                  {item.id === 'report' && (
                    <form onSubmit={handleReportProblem} className="pt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Emne
                        </label>
                        <select
                          value={reportForm.subject}
                          onChange={(e) => setReportForm(prev => ({ ...prev, subject: e.target.value }))}
                          required
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                        >
                          <option value="">Velg emne</option>
                          <option value="bug">Teknisk feil</option>
                          <option value="abuse">Misbruk/svindel</option>
                          <option value="feedback">Tilbakemelding</option>
                          <option value="other">Annet</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A1929] mb-1">
                          Beskrivelse
                        </label>
                        <textarea
                          value={reportForm.description}
                          onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                          required
                          rows={4}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] resize-none"
                          placeholder="Beskriv problemet eller tilbakemeldingen..."
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#0A1929] text-white py-2 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50"
                      >
                        Send rapport
                      </button>
                    </form>
                  )}
                </div>
              )}

              {index < menuItems.length - 1 && <div className="border-b border-slate-100" />}
            </div>
          ))}
        </div>

        {/* Version Info */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Minitask v1.0.0 • Utviklet med kjærlighet i Norge
        </p>
      </div>
    </div>
  );
};

export default SettingsPage;
